import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Icon } from '../Icon/Icon';
const TabsContext = React.createContext({});
/**
 * Hook to manage external tab selection.
 * @param {string} [defaultSelected] - A tab `[id]` attribute value.
 * @return {[unknown, ((value: unknown) => void)]}
 */
export const useTabSelector = (defaultSelected) => {
    const [selected, setSelected] = useState(defaultSelected);
    return [selected, setSelected];
};
/**
 * Bronson Tabs component.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-tabs.html
 * @constructor
 */
export function Tabs({ children, className, defaultSelected, onChangeSelected, selected, testId, ...otherProps }) {
    /**
     * Controlled mode is determined by presence of `selected` prop (not by `defaultSelected`).
     */
    const isControlled = selected !== undefined;
    /**
     * Internal state is used only in uncontrolled mode.
     * Ensure stable string value to avoid transient undefined states.
     */
    const [currSelected, setCurrSelected] = useState(defaultSelected ?? '');
    /**
     * If user changes `defaultSelected` in uncontrolled mode, keep internal state in sync.
     * (No-op in controlled mode.)
     */
    useEffect(() => {
        if (!isControlled && defaultSelected) {
            setCurrSelected(defaultSelected);
        }
    }, [defaultSelected, isControlled]);
    /**
     * Build context based on controlled/uncontrolled mode.
     */
    const uncontrolledCtx = useMemo(() => ({
        defaultSelected,
        isSelected: (tabId) => currSelected === tabId,
        selected: currSelected,
        setSelected: (tabId) => setCurrSelected(tabId),
    }), [defaultSelected, currSelected]);
    const controlledCtx = useMemo(() => ({
        defaultSelected: selected,
        isSelected: (tabId) => selected === tabId,
        selected,
        setSelected: (tabId) => onChangeSelected?.(tabId),
    }), [selected, onChangeSelected]);
    const contextValue = isControlled ? controlledCtx : uncontrolledCtx;
    const classNameList = classNames('c-tabs', className).trim();
    return (React.createElement("div", { ...otherProps, className: classNameList, "data-testid": testId },
        React.createElement(TabsContext.Provider, { value: contextValue }, children)));
}
Tabs.displayName = 'Tabs';
/**
 * Bronson TabsHeader component (nested).
 * @constructor
 */
function TabsHeader({ children, className, icons, iconsOnly, stacked, testId, ...otherProps }) {
    const tabsClassNameList = classNames('c-tabs__nav', {
        'c-tabs__nav--icons': icons,
        'c-tabs__nav--icons-stacked': stacked,
        'c-tabs__nav--icons-only': iconsOnly,
    }, className).trim();
    const tabRefs = React.useRef([]);
    const items = React.Children.toArray(children);
    const childrenWithProps = items.map((child, index) => {
        if (!React.isValidElement(child))
            return child;
        return React.cloneElement(child, {
            tabRef: (el) => {
                tabRefs.current[index] = el;
            },
            index,
            allRefs: tabRefs.current,
        });
    });
    return (React.createElement("div", { className: "c-tabs__nav-container" },
        React.createElement("div", { className: tabsClassNameList, "data-testid": testId, role: "tablist", ...otherProps }, childrenWithProps)));
}
TabsHeader.displayName = 'Tabs.Header';
Tabs.Header = TabsHeader;
/**
 * Bronson TabsHeaderItem component (nested).
 * @constructor
 */
function TabsHeaderItem({ allRefs = [], children, className, disabled, icon, index = 0, tabId, tabRef, testId, ...otherProps }) {
    const context = React.useContext(TabsContext);
    const buttonClassNameList = classNames('c-tabs__nav-link', className).trim();
    const scrollToTabIfNeeded = (el) => {
        if (!el)
            return;
        const bounding = el.getBoundingClientRect();
        const isVisible = bounding.left >= 0 && bounding.right <= window.innerWidth;
        if (!isVisible) {
            el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
    };
    const handleKeyDown = (event) => {
        const prev = () => allRefs[(index - 1 + allRefs.length) % allRefs.length];
        const next = () => allRefs[(index + 1) % allRefs.length];
        const first = () => allRefs[0];
        const last = () => allRefs[allRefs.length - 1];
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                next()?.focus();
                break;
            case 'ArrowLeft':
                event.preventDefault();
                prev()?.focus();
                break;
            case 'Home':
                event.preventDefault();
                first()?.focus();
                break;
            case 'End':
                event.preventDefault();
                last()?.focus();
                break;
            case 'ArrowDown': {
                event.preventDefault();
                const selectedTabId = context?.selected;
                if (selectedTabId) {
                    const panel = document.getElementById(`panel-${selectedTabId}`);
                    panel?.focus();
                }
                break;
            }
            case 'Enter':
            case ' ':
                event.preventDefault();
                if (!disabled) {
                    context?.setSelected?.(tabId);
                    scrollToTabIfNeeded(event.currentTarget);
                }
                break;
        }
    };
    const iClassNameList = classNames('c-tabs__icon').trim();
    return (React.createElement("div", { className: "c-tabs__nav-item", "data-testid": testId, ...otherProps },
        React.createElement("button", { "aria-controls": `panel-${tabId}`, "aria-disabled": disabled, "aria-selected": !disabled && context?.isSelected?.(tabId), className: buttonClassNameList, id: tabId, ref: tabRef, role: "tab", tabIndex: !disabled && context?.isSelected?.(tabId) ? undefined : -1, type: "button", onClick: (e) => {
                if (!disabled) {
                    context?.setSelected?.(tabId);
                    scrollToTabIfNeeded(e.currentTarget);
                }
            }, onKeyDown: handleKeyDown },
            icon && React.createElement(Icon, { className: iClassNameList, name: icon }),
            React.createElement("span", { className: "c-tabs__text" }, children))));
}
TabsHeaderItem.displayName = 'Tab.HeaderItem';
Tabs.HeaderItem = TabsHeaderItem;
/**
 * Bronson TabsContent component (nested).
 * @constructor
 */
function TabsContent({ children, testId, ...otherProps }) {
    return (React.createElement("div", { "data-testid": testId, className: "c-tabs__content-wrapper", ...otherProps }, children));
}
TabsContent.displayName = 'Tabs.Content';
Tabs.Content = TabsContent;
/**
 * Bronson TabsContentItem component (nested).
 * @constructor
 */
function TabsContentItem({ children, className, tabId, testId, ...otherProps }) {
    const context = React.useContext(TabsContext);
    const classNameList = classNames('c-tabs__content', className).trim();
    const isSelected = !!context?.isSelected?.(tabId);
    return (
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    React.createElement("div", { ...otherProps, "aria-hidden": isSelected ? 'false' : 'true', "aria-labelledby": tabId, className: classNameList, "data-testid": testId, id: `panel-${tabId}`, 
        // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
        inert: isSelected ? null : '', role: "tabpanel", tabIndex: -1 }, children));
}
TabsContentItem.displayName = 'Tabs.ContentItem';
Tabs.ContentItem = TabsContentItem;
